import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { ChangePasswordModal, PrivacyPolicyModal, TermsAndConditionsModal } from '../../components/modals';
import { getFirstAccessibleRoute } from '../../routes/ProtectedRoute';

interface LoginProps {
  setIsAuthenticated: (auth: boolean) => void;
}

const Login: React.FC<LoginProps> = ({ setIsAuthenticated }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const [archivedClientName, setArchivedClientName] = useState<string>('');
  const [suspendedModal, setSuspendedModal] = useState<{
    message: string;
    teamName?: string;
  } | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if user is already authenticated
  useEffect(() => {
    if (sessionManager.isAuthenticated()) {
      setIsAuthenticated(true);
      // Redirect based on role
      navigate(getFirstAccessibleRoute());
      return;
    }

    // If redirected here because the account was archived, show the archived popup
    const archivedFlag = new URLSearchParams(window.location.search).get('archived');
    const inactiveFlag = new URLSearchParams(window.location.search).get('inactive');
    const suspendedFlag = new URLSearchParams(window.location.search).get('suspended');
    const storedClientName = sessionStorage.getItem('archivedClientName') || '';
    if (archivedFlag === '1') {
      setArchivedClientName(storedClientName);
      setShowArchivedModal(true);
      sessionStorage.removeItem('archivedClientName');
      return;
    }

    if (inactiveFlag === '1') {
      setShowInactiveModal(true);
      return;
    }

    if (suspendedFlag === '1') {
      setSuspendedModal({
        message:
          'Your team account has been suspended due to an unpaid invoice. Please contact support@statdog.live or have your team administrator pay the outstanding invoice to restore access.',
      });
    }
  }, [setIsAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (email.length === 0 || password.length === 0) {
      showErrorMsg('Please enter email and password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post(API_URLS.LOGIN, {
        email,
        password,
      });

      if (response.data.success) {
        const payload: any = response.data.data ? response.data.data : response.data;
        const user = payload.user || {};
        const token = payload.token;
        const refreshToken = payload.refreshToken; // Get refresh token
        if (!token) {
          showErrorMsg('Login failed: no token received');
          setIsLoading(false);
          return;
        }

        if (payload.requiresPasswordChange) {
          const tempSessionData: any = {
            token,
            refreshToken, // Store refresh token
            user: {
              id: user.id,
              email: user.email || email,
              name: user.name || email,
              role: user.role, // Use single role object from backend
              permissions: user.permissions,
              customPermissions: user.customPermissions,
            },
            clientId: user.clientId,
            isAuthenticated: false,
            lastValidated: Date.now(), // Track validation time
          };
          sessionManager.saveSession(tempSessionData);
          setShowChangePasswordModal(true);
          setIsLoading(false);
          return;
        }

        const sessionData: any = {
          token,
          refreshToken, // Store refresh token
          user: {
            id: user.id,
            email: user.email || email,
            name: user.name || email,
            role: user.role, // Use single role object from backend
            permissions: user.permissions,
            customPermissions: user.customPermissions,
          },
          clientId: user.clientId,
          isAuthenticated: true,
          lastValidated: Date.now(), // Track validation time
        };
        sessionManager.saveSession(sessionData);

        // First invited lead coach: show welcome modal, then route to Billing on Get Started
        if (payload.isFirstCoachLogin) {
          sessionStorage.setItem('showWelcomeModal', 'true');
          sessionStorage.setItem('welcomeClientName', payload.client?.name || '');
          sessionStorage.setItem('welcomeTargetRoute', '/billing');
          setIsAuthenticated(true);
          navigate('/players');
        } else {
          showSuccessMsg(payload.message || 'Login successful!');
          setIsAuthenticated(true);
          navigate(getFirstAccessibleRoute());
        }
      } else {
        // Archived client account should always show popup (not toast)
        if (response.data.code === 'CLIENT_ARCHIVED' && response.data.client) {
          const clientName = response.data.client.name || '';
          setArchivedClientName(clientName);
          setShowArchivedModal(true);
          return;
        }
        if (response.data.code === 'ACCOUNT_INACTIVE') {
          setShowInactiveModal(true);
          return;
        }
        if (response.data.code === 'ACCOUNT_SUSPENDED_PAYMENT') {
          setSuspendedModal({
            message:
              response.data.message ||
              'Your account has been suspended due to an unpaid invoice. Please contact support@statdog.live for help.',
            teamName: response.data.client?.name,
          });
          return;
        }

        // Generic login failure from backend
        showErrorMsg(response.data.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);

      // Handle archived client account on login attempt
      if (error.response?.data?.code === 'CLIENT_ARCHIVED' && error.response?.data?.client) {
        const clientName = error.response.data.client.name || '';
        setArchivedClientName(clientName);
        setShowArchivedModal(true);
        return;
      }
      if (error.response?.data?.code === 'ACCOUNT_INACTIVE') {
        setShowInactiveModal(true);
        return;
      }
      if (error.response?.data?.code === 'ACCOUNT_SUSPENDED_PAYMENT') {
        setSuspendedModal({
          message:
            error.response.data.message ||
            'Your account has been suspended due to an unpaid invoice. Please contact support@statdog.live for help.',
          teamName: error.response.data.client?.name,
        });
        return;
      }

      if (error.response?.data?.message) {
        showErrorMsg(error.response.data.message);
      } else {
        showErrorMsg('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChanged = () => {
    const currentSession = sessionManager.getSession();

    if (currentSession) {
      const updatedSessionData = {
        ...currentSession,
        isAuthenticated: true,
      };

      sessionManager.saveSession(updatedSessionData);
      setIsAuthenticated(true);
      setShowChangePasswordModal(false);

      // Redirect based on role
      navigate(getFirstAccessibleRoute());
    } else {
      console.error('No current session found!'); // Debug log
    }
  };

  const handleClosePasswordModal = () => {
    setShowChangePasswordModal(false);
    sessionManager.clearSession();
    // Clear the form
    setEmail('');
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <img src="/images/logo-stacked.png" alt="STATDOG Logo" className="h-32 w-auto" />
        </div>

        {/* Login Card */}
        <div className="sm:bg-white sm:shadow-lg sm:rounded-2xl sm:border border-gray-100 py-8 px-4 sm:px-10">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">Welcome Back</h2>
            <p className="mt-2 text-sm text-gray-600">Sign in to your STATDOG account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Billing Error Banner */}
            {billingError && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Account Suspended - Payment Required</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{billingError}</p>
                    </div>
                    <div className="mt-3">
                      <a
                        href="mailto:support@statdog.live?subject=Account%20Reactivation%20Request"
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Contact Support
                      </a>
                      <button
                        type="button"
                        onClick={() => setBillingError(null)}
                        className="ml-2 text-xs text-red-600 hover:text-red-800 underline"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                    />
                  </svg>
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="appearance-none block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.956 9.956 0 012.01-3.368m2.148-2.149A9.956 9.956 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.955 9.955 0 01-4.043 5.058M15 12a3 3 0 11-6 0 3 3 0 016 0zm6 9L3 3"
                      />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.522 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S3.732 16.057 2.458 12z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Signing in...</span>
                  </div>
                ) : (
                  <span>Sign In</span>
                )}
              </button>
            </div>

            {/* Forgot Password Link */}
            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-gray-600 hover:text-gray-500 transition-colors duration-200 hover:underline"
              >
                Forgot your password?
              </Link>
            </div>

            {/* Sign Up Link */}
            <div className="text-center">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">New to STATDOG?</span>
                </div>
              </div>
              <div className="mt-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center w-full py-2.5 px-4 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors duration-200 font-medium"
                >
                  Create Your Team Account
                </Link>
              </div>
            </div>

            {/* Employee Login Link */}
            <div className="text-center">
              <Link
                to="/employee-login"
                className="text-sm text-gray-500 hover:text-gray-600 transition-colors duration-200"
              >
                Access Employee Portal →
              </Link>
            </div>
          </form>

          {/* Legal Links */}
          <div className="mt-5 pt-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 text-xs text-gray-500">
              <button
                onClick={() => setShowPrivacyModal(true)}
                className="hover:text-green-600 transition-colors duration-200 underline"
              >
                Privacy Policy
              </button>
              <span className="hidden sm:block">•</span>
              <button
                onClick={() => setShowTermsModal(true)}
                className="hover:text-green-600 transition-colors duration-200 underline"
              >
                Terms & Conditions
              </button>
            </div>
            <p className="text-center text-xs text-gray-400 mt-2">
              © 2026 Sports Data Partners, LLC dba STATDOG. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={handleClosePasswordModal}
        userEmail={email}
        onPasswordChanged={handlePasswordChanged}
      />

      <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />

      <TermsAndConditionsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />

      {/* Archived Account Modal */}
      {showArchivedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 backdrop-blur-sm px-4">
          <div className="relative overflow-hidden bg-white rounded-3xl shadow-2xl max-w-xl w-full border border-emerald-100">
            <div className="h-2 w-full bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500" />
            <div className="p-8 sm:p-9">
              <div className="flex items-start gap-4 mb-6">
                <div className="shrink-0 w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shadow-sm">
                  <svg
                    className="w-7 h-7 text-emerald-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M6 10V8a6 6 0 1112 0v2"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <rect x="4" y="10" width="16" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M3.5 3.5l17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-[0.16em] uppercase text-emerald-600 mb-1">
                    STATDOG Notice
                  </p>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight">Account Archived</h2>
                </div>
              </div>

              <div className="space-y-4 text-[15px] leading-7 text-slate-700">
                <p>
                  Hello from <span className="font-semibold text-slate-900">STATDOG</span>!
                </p>
                <p>
                  The <span className="font-semibold text-slate-900">{archivedClientName || 'STATDOG'}</span> account
                  has been archived and is no longer active. To re-activate this account, please contact our team at{' '}
                  <a
                    href="mailto:support@statdog.live"
                    className="font-semibold text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
                  >
                    support@statdog.live
                  </a>
                  .
                </p>
                <p>
                  Thank you for being part of the STATDOG community.
                  <br />
                  <span className="font-semibold text-slate-900">The STATDOG Team</span>
                </p>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:justify-end">
                <a
                  href="mailto:support@statdog.live"
                  className="inline-flex justify-center items-center px-4 py-2.5 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-colors"
                >
                  Contact Support
                </a>
                <button
                  type="button"
                  onClick={() => setShowArchivedModal(false)}
                  className="inline-flex justify-center items-center px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suspended account (billing) modal */}
      {suspendedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 backdrop-blur-sm px-4">
          <div className="relative overflow-hidden bg-white rounded-3xl shadow-2xl max-w-xl w-full border border-rose-100">
            <div className="h-2 w-full bg-gradient-to-r from-rose-600 via-red-500 to-red-600" />
            <div className="p-8 sm:p-9">
              <div className="flex items-start gap-4 mb-6">
                <div className="shrink-0 w-14 h-14 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center shadow-sm">
                  <svg
                    className="w-7 h-7 text-rose-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 9v4m0 4h.01M5.07 19h13.86a1 1 0 00.87-1.48l-6.93-12a1 1 0 00-1.74 0l-6.93 12A1 1 0 005.07 19z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-[0.16em] uppercase text-rose-600 mb-1">STATDOG Notice</p>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight">
                    Account suspended
                  </h2>
                  {suspendedModal.teamName ? (
                    <p className="mt-2 text-sm font-medium text-slate-600">
                      Team: <span className="text-slate-900">{suspendedModal.teamName}</span>
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="space-y-4 text-[15px] leading-7 text-slate-700">
                <p>{suspendedModal.message}</p>
                <p>
                  Questions about billing? Email{' '}
                  <a
                    href="mailto:support@statdog.live"
                    className="font-semibold text-rose-700 underline underline-offset-2 hover:text-rose-800"
                  >
                    support@statdog.live
                  </a>
                  .
                </p>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:justify-end">
                <a
                  href="mailto:support@statdog.live"
                  className="inline-flex justify-center items-center px-4 py-2.5 text-sm font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl hover:bg-rose-100 transition-colors"
                >
                  Contact support
                </a>
                <button
                  type="button"
                  onClick={() => setSuspendedModal(null)}
                  className="inline-flex justify-center items-center px-4 py-2.5 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inactive User Modal */}
      {showInactiveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 backdrop-blur-sm px-4">
          <div className="relative overflow-hidden bg-white rounded-3xl shadow-2xl max-w-xl w-full border border-amber-100">
            <div className="h-2 w-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500" />
            <div className="p-8 sm:p-9">
              <div className="flex items-start gap-4 mb-6">
                <div className="shrink-0 w-14 h-14 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center shadow-sm">
                  <svg
                    className="w-7 h-7 text-amber-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M12 8v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="12" cy="16.5" r="1" fill="currentColor" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-[0.16em] uppercase text-amber-600 mb-1">
                    STATDOG Notice
                  </p>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight">Account Inactive</h2>
                </div>
              </div>

              <div className="space-y-4 text-[15px] leading-7 text-slate-700">
                <p>Your user account is currently marked as inactive, so access is restricted.</p>
                <p>
                  Please contact your team administrator to reactivate your account, or reach out to{' '}
                  <a
                    href="mailto:support@statdog.live"
                    className="font-semibold text-amber-700 underline underline-offset-2 hover:text-amber-800"
                  >
                    support@statdog.live
                  </a>{' '}
                  if you need assistance.
                </p>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:justify-end">
                <a
                  href="mailto:support@statdog.live"
                  className="inline-flex justify-center items-center px-4 py-2.5 text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition-colors"
                >
                  Contact Support
                </a>
                <button
                  type="button"
                  onClick={() => setShowInactiveModal(false)}
                  className="inline-flex justify-center items-center px-4 py-2.5 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
